import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Button,
  IconButton,
  Tooltip,
  TextField,
  Modal,
} from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon, Edit as EditIcon } from '@mui/icons-material';
import CharacterImageUpload from './CharacterImageUpload';
import BackgroundImageUpload from './BackgroundImageUpload'; // Import the new component
import BackgroundMusicUpload from './BackgroundMusicUpload'; 

const StoryboardColumn = ({ story,  isEditable = true, onRemoveDialogue, onAddDialogue, onEditDialogue, onAddCharacter, showSelection = false, onSelect, onUpdateCharacterImage, assets, onUpdateBackgroundImage, onUpdateBackgroundMusic}) => {
  const [hoveredCard, setHoveredCard] = useState(null); // Track which card is hovered
  const [editingIndex, setEditingIndex] = useState(null); // Track which dialogue is being edited
  const [editText, setEditText] = useState(''); // Track the edited text
  const [isAddCharacterModalOpen, setIsAddCharacterModalOpen] = useState(false); // Track if the add character modal is open
  const [newCharacterName, setNewCharacterName] = useState(''); // Track new character name
  const [newCharacterDescription, setNewCharacterDescription] = useState(''); // Track new character description
  const [characterImages] = useState({}); // Track character images
  // Handle saving edited text
  const handleSaveEdit = (index) => {
    onEditDialogue(index, editText);
    setEditingIndex(null); // Exit edit mode
    setEditText(''); // Clear the edit text
  };

  // Handle cycling through characters
  const handleCharacterChange = (index) => {
    const currentDialogue = story.dialogue[index]; // Get the current dialogue
    const currentCharacter = currentDialogue.character; // Get the current character
    const currentCharacterIndex = story.characters.findIndex((c) => c.name === currentCharacter); // Find the index of the current character
    const nextCharacterIndex = (currentCharacterIndex + 1) % story.characters.length; // Cycle to the next character
    const nextCharacter = story.characters[nextCharacterIndex].name; // Get the next character

    // Update the dialogue with the new character
    const updatedDialogue = { ...currentDialogue, character: nextCharacter };

    // Update the story with the new dialogue
    onEditDialogue(index, updatedDialogue.text, nextCharacter); // Pass the new character to the parent
  };

  // Handle adding a new character
  const handleAddCharacter = () => {
    if (newCharacterName.trim() && newCharacterDescription.trim()) {
      const newCharacter = {
        name: newCharacterName,
        description: newCharacterDescription,
      };
      onAddCharacter(newCharacter, characterImages[newCharacter.name]); // Pass the new character to the parent
      setIsAddCharacterModalOpen(false); // Close the modal
      setNewCharacterName(''); // Clear the name field
      setNewCharacterDescription(''); // Clear the description field
    }
  };

    // Use assets to fetch character images
    const getCharacterImage = (characterName) => {
      return assets.characters[characterName] || null; // Return null if no image is available
    };

  return (
    <Box
      sx={{
        height: '75vh',           // Set fixed height to 75% of the viewport
        alignSelf: 'flex-start',  // Prevent parent from stretching this box
        width: '100%',
        maxWidth: '800px',
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        ...(showSelection && {
          width: '45%',
          minWidth: 400,
        }),
      }}
    >
      {/* Inner scrollable container */}

        <Typography variant="h5" gutterBottom>
          {story.title}
        </Typography>
  
        {/* Character List */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {story.characters.map((character, index) => (
          <Tooltip key={index} title={character.description} arrow>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                p: 1,
                borderRadius: 1,
              }}
            >
              <CharacterImageUpload
                onImageUpload={(characterName, image) => onUpdateCharacterImage(characterName, image)}
                character={{ ...character, image: getCharacterImage(character.name) }}
                assets={assets}
              />
              <Typography variant="body1">{character.name}</Typography>
            </Box>
          </Tooltip>
        ))}
            {/* Add Character Button */}
            <Button
              variant="outlined"
              sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1 }}
              onClick={() => setIsAddCharacterModalOpen(true)}
            >
              <AddIcon fontSize="small" />
              Add Character
            </Button>
          </Box>
        </Box>

      {/* BG Image and BG Music Upload Buttons */}
      <Box
        sx={{
          display: 'flex',
          gap: 2, // Add gap between the buttons
          justifyContent: 'left',
          mb: 2, // Add margin to separate from other elements
        }}
      >
        {/* BG Image Upload */}
        <BackgroundImageUpload
          onUpdateBackgroundImage={onUpdateBackgroundImage}
          assets={assets}
        />

        {/* BG Music Upload */}
        <BackgroundMusicUpload
          onUpdateBackgroundMusic={onUpdateBackgroundMusic}
          assets={assets}
        />
      </Box>
  
        {/* Add Character Modal */}
        <Modal
          open={isAddCharacterModalOpen}
          onClose={() => setIsAddCharacterModalOpen(false)}
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <Box
            sx={{
              backgroundColor: 'white',
              p: 3,
              borderRadius: 2,
              width: '400px',
            }}
          >
            <Typography variant="h6" gutterBottom>
              Add New Character
            </Typography>
            <TextField
              label="Name"
              fullWidth
              value={newCharacterName}
              onChange={(e) => setNewCharacterName(e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={3}
              value={newCharacterDescription}
              onChange={(e) => setNewCharacterDescription(e.target.value)}
              sx={{ mb: 2 }}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleAddCharacter}
              disabled={!newCharacterName.trim() || !newCharacterDescription.trim()}
            >
              Save Character
            </Button>
          </Box>
        </Modal>
  
        {/* Dialogue Cards */}
        {story.dialogue.map((dialogue, index) => {
          const character = story.characters.find((c) => c.name === dialogue.character);
          const characterImage = getCharacterImage(character.name); // Use assets to fetch character image
          const alignment = index % 2 === 0 ? 'left' : 'right'; // Alternate alignment
  
          return (
            <Box
              key={index}
              sx={{
                display: 'flex',
                justifyContent: alignment === 'left' ? 'flex-start' : 'flex-end',
                mb: 2,
              }}
              onMouseEnter={() => setHoveredCard(index)}
              onMouseLeave={() => setHoveredCard(null)}
            >
              <Card
                sx={{
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  color: 'white',
                  p: 2,
                  borderRadius: 2,
                  boxShadow: 3,
                  maxWidth: '70%',
                  position: 'relative',
                }}
              >

              {/* Icons for Remove, Add, Edit */}
              {hoveredCard === index && isEditable && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    display: 'flex',
                    gap: 1,
                  }}
                >
                  <Tooltip title="Remove Dialogue">
                    <IconButton
                      onClick={() => onRemoveDialogue(index)}
                      sx={{ color: 'white', fontSize: 'small', padding: '4px' }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Add Dialogue">
                    <IconButton
                      onClick={() => onAddDialogue(index)}
                      sx={{ color: 'white', fontSize: 'small', padding: '4px' }}
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit Dialogue">
                    <IconButton
                      onClick={() => {
                        setEditingIndex(index);
                        setEditText(dialogue.text);
                      }}
                      sx={{ color: 'white', fontSize: 'small', padding: '4px' }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
                
                {/* ... dialogue card content (icons, text, etc.) ... */}
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {alignment === 'left' && (
                      <Tooltip title={character.description} arrow>
                      <Avatar
                        src={characterImage} // Use the character's image from assets
                        sx={{
                          backgroundColor: isEditable ? '#007bff' : '#666',
                          cursor: isEditable ? 'pointer' : 'default',
                        }}
                        onClick={() => handleCharacterChange(index)}
                      >
                        {!characterImage && character.name[0]} {/* Fallback to first letter if no image */}
                      </Avatar>
                      </Tooltip>
                    )}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
                      <Typography
                        variant="h6"
                        color="white"
                        sx={{ textAlign: alignment === 'right' ? 'right' : 'left' }}
                      >
                        {character.name}
                      </Typography>
                      {editingIndex === index ? (
                        <TextField
                          fullWidth
                          variant="outlined"
                          value={editText}
                          onChange={(e) => setEditText(e.target.value)}
                          onBlur={() => handleSaveEdit(index)}
                          onKeyPress={(e) => e.key === 'Enter' && handleSaveEdit(index)}
                          sx={{ backgroundColor: 'white', borderRadius: 1 }}
                        />
                      ) : (
                        <Typography variant="body1">{dialogue.text}</Typography>
                      )}
                    </Box>
                    {alignment === 'right' && (
                      <Tooltip title={character.description} arrow>
                        <Avatar
                        src={characterImage} // Use the character's image from assets
                          sx={{ backgroundColor: '#ff9900', cursor: 'pointer' }}
                          onClick={() => handleCharacterChange(index)}
                        >
                        {!characterImage && character.name[0]} {/* Fallback to first letter if no image */}
                        </Avatar>
                      </Tooltip>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Box>
          );
        })}
  
        {/* Confirm Button and additional selection UI (if applicable) */}
        {showSelection && (
          <Box
            sx={{
              mt: 4,
              textAlign: 'center',
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              padding: 2,
              borderRadius: 2,
              mb: 4,
            }}
          >
            <Button
              variant="contained"
              color="primary"
              onClick={onSelect}
              sx={{
                fontWeight: 'bold',
                textTransform: 'none',
                fontSize: '1.1rem',
                px: 4,
                py: 1.5,
              }}
            >
              Use This Version
            </Button>
          </Box>
        )}
        <Box
            sx={{
              mt: 4,
              backgroundColor: 'rgba(0, 0, 0, 0.0)',
              padding: 2,
              paddingBottom : 10  ,
              borderRadius: 2,
              mb: 4,
            }}
          ></Box>
      </Box>
  );  
};

export default StoryboardColumn;